import { defineStore } from 'pinia'
import { mande } from 'mande'
import { useI18n } from 'vue-i18n'
import { baseDec } from '@/composables/utilities/general'
import { useHelpdeskChatStore } from '@/store/helpdeskChat'

interface User {
  info: {
    email: string | null
    name: string | null
    mobile: string | null
    kyc_status?: string | null
    first_name: string | null
  }
  isAuthenticated: boolean
  token: string | null
}

export const useUserStore = defineStore('user', {
  state: () => ({
    user: {
      info: {
        email: null,
        name: null,
        mobile: null,
        first_name: null,
      },
      isAuthenticated: false,
      token: null,
    } as User,
    // Hem server hem client'ta true başlasın: SSR HTML'inde auth-bound UI render
    // edilmesin, client hydrate olduğunda loading overlay görünüp auth resolve olana
    // kadar bekletilsin. Aksi halde SSR'da render olan logged-out hero (örn. "Nasıl
    // yardımcı olabiliriz?") logged-in welcome'a geçerken görsel atlama oluyor.
    isInitializing: true,
  }),

  getters: {
    isLoggedIn: (state) => {
      return state.user.isAuthenticated
    },
  },

  actions: {
    addAuthToken(token: string): void {
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem('auth-token', token)
        } catch {}
      }
      this.user.token = token
    },

    async initializeClientAuth(): Promise<boolean> {
      if (typeof window === 'undefined') {
        this.isInitializing = false
        return false
      }

      this.isInitializing = true

      try {
        let token: string | null = null
        try {
          token = localStorage.getItem('auth-token')
        } catch {}

        if (!token) {
          this.user = {
            info: { email: null, name: null, mobile: null, first_name: null },
            isAuthenticated: false,
            token: null,
          }
          this.isInitializing = false
          return false
        }

        this.user.token = token
        const success = await this.fetchUser()
        this.isInitializing = false
        return success
      } catch {
        try {
          localStorage.removeItem('auth-token')
        } catch {}
        this.user = {
          info: { email: null, name: null, mobile: null, first_name: null },
          isAuthenticated: false,
          token: null,
        }
        this.isInitializing = false
        return false
      }
    },

    async fetchUser() {
      try {
        let token: string | null = null
        let userSessionId: string | null = null
        if (typeof window !== 'undefined') {
          try {
            token = localStorage.getItem('auth-token')
            userSessionId = localStorage.getItem('user-session-id')
          } catch {}
        }
        if (!token) {
          this.user = {
            info: { email: null, name: null, mobile: null, first_name: null },
            isAuthenticated: false,
            token: null,
          }
          return false
        }

        const headers: Record<string, string> = {
          'Accept-Language': useI18n().locale.value.split('-')[0],
          Authorization: `${token}`,
          device: navigator.userAgent,
          platform: (navigator as any).userAgentData?.platform || navigator.platform || 'unknown',
        }

        if (userSessionId) {
          headers['pragma-cache-local'] = baseDec(userSessionId)
        }

        const config = useRuntimeConfig()
        const api = mande(config.public.apiBaseUrl)
        const response = await api.get<any>('user', { headers })

        this.user.info.email = response.payload.info.email
        this.user.info.name = response.payload.info.name
        this.user.info.first_name = response.payload.info.first_name || null
        this.user.info.mobile = response.payload.info.mobile
        this.user.info.kyc_status = response.payload.info.kyc_status || null
        this.user.isAuthenticated = true
        this.user.token = token
        return true
      } catch (error: any) {
        const status = error?.status || error?.response?.status
        if (status === 403 || status === 401) {
          try {
            if (typeof window !== 'undefined') {
              localStorage.removeItem('auth-token')
            }
          } catch {}
        }
        this.user = {
          info: { email: null, name: null, mobile: null, first_name: null },
          isAuthenticated: false,
          token: null,
        }
        return false
      }
    },

    signOut(): void {
      try {
        useHelpdeskChatStore().clearState()
      } catch {
        /* store may not exist if user never opened chat */
      }
      try {
        localStorage.removeItem('auth-token')
      } catch {}
      this.user = {
        info: { email: null, name: null, mobile: null, first_name: null },
        isAuthenticated: false,
        token: null,
      }
      if (typeof window !== 'undefined') window.location.reload()
    },
  },
})
